import { useEffect, useMemo, useState } from 'react'
import { DesignSystem, DesignSystemStore, DesignTokens, MotifId } from '../types'
import { getMotifById } from '../data/motifs'
import { FONT_WEIGHT_VALUES, getFontOption } from '../data/typography'
import { buildGoogleFontsUrl } from '../utils/fontLoader'

const STORAGE_KEY = 'nimble-design-systems-v1'

const generateId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `system_${Date.now()}`

const createDefaultSystem = (): DesignSystem => {
  const now = new Date().toISOString()
  return {
    id: generateId(),
    name: 'Primary System',
    baseMotifId: 'professional',
    overrides: {},
    themeName: 'Nimble Theme',
    createdAt: now,
    updatedAt: now,
  }
}

const DEFAULT_STORE: DesignSystemStore = {
  activeId: '',
  systems: [],
}

const getSystemById = (systems: DesignSystem[], id: string): DesignSystem | undefined =>
  systems.find((system) => system.id === id)

export function useDesignSystems() {
  const [store, setStore] = useState<DesignSystemStore>(() => {
    if (typeof window === 'undefined') return DEFAULT_STORE

    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        return JSON.parse(stored) as DesignSystemStore
      }
    } catch {
      // Ignore parse errors
    }

    const defaultSystem = createDefaultSystem()
    return {
      activeId: defaultSystem.id,
      systems: [defaultSystem],
    }
  })

  useEffect(() => {
    if (store.systems.length === 0) {
      const defaultSystem = createDefaultSystem()
      setStore({
        activeId: defaultSystem.id,
        systems: [defaultSystem],
      })
      return
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(store))
  }, [store])

  const activeSystem = useMemo(() => {
    const system = getSystemById(store.systems, store.activeId)
    return system ?? store.systems[0] ?? createDefaultSystem()
  }, [store.activeId, store.systems])

  const resolvedTokens = useMemo((): DesignTokens => {
    const motif = getMotifById(activeSystem.baseMotifId)
    const baseTokens = motif ? motif.tokens : getMotifById('professional')!.tokens
    return {
      ...baseTokens,
      ...activeSystem.overrides,
    }
  }, [activeSystem.baseMotifId, activeSystem.overrides])

  useEffect(() => {
    const url = buildGoogleFontsUrl([
      {
        font: getFontOption(resolvedTokens.displayFont),
        weight: FONT_WEIGHT_VALUES[resolvedTokens.displayWeight],
      },
      {
        font: getFontOption(resolvedTokens.bodyFont),
        weight: FONT_WEIGHT_VALUES[resolvedTokens.bodyWeight],
      },
      {
        font: getFontOption(resolvedTokens.monoFont),
        weight: FONT_WEIGHT_VALUES[resolvedTokens.monoWeight],
      },
    ])

    if (!url || typeof document === 'undefined') return

    const existing = document.getElementById('nimble-google-fonts') as HTMLLinkElement | null
    if (existing) {
      existing.href = url
      return
    }

    const link = document.createElement('link')
    link.id = 'nimble-google-fonts'
    link.rel = 'stylesheet'
    link.href = url
    document.head.appendChild(link)
  }, [
    resolvedTokens.displayFont,
    resolvedTokens.displayWeight,
    resolvedTokens.bodyFont,
    resolvedTokens.bodyWeight,
    resolvedTokens.monoFont,
    resolvedTokens.monoWeight,
  ])

  const updateSystem = (id: string, updater: (system: DesignSystem) => DesignSystem) => {
    setStore((prev) => ({
      ...prev,
      systems: prev.systems.map((system) =>
        system.id === id ? updater(system) : system
      ),
    }))
  }

  const selectSystem = (id: string) => {
    setStore((prev) => ({
      ...prev,
      activeId: id,
    }))
  }

  const createSystem = (name: string, motifId: MotifId) => {
    const now = new Date().toISOString()
    const newSystem: DesignSystem = {
      id: generateId(),
      name,
      baseMotifId: motifId,
      overrides: {},
      themeName: `${name} Theme`,
      createdAt: now,
      updatedAt: now,
    }
    setStore((prev) => ({
      activeId: newSystem.id,
      systems: [newSystem, ...prev.systems],
    }))
  }

  const setMotif = (motifId: MotifId) => {
    updateSystem(activeSystem.id, (system) => ({
      ...system,
      baseMotifId: motifId,
      overrides: {},
      updatedAt: new Date().toISOString(),
    }))
  }

  const updateToken = <K extends keyof DesignTokens>(key: K, value: DesignTokens[K]) => {
    updateSystem(activeSystem.id, (system) => ({
      ...system,
      overrides: {
        ...system.overrides,
        [key]: value,
      },
      updatedAt: new Date().toISOString(),
    }))
  }

  const resetOverrides = () => {
    updateSystem(activeSystem.id, (system) => ({
      ...system,
      overrides: {},
      updatedAt: new Date().toISOString(),
    }))
  }

  const setThemeName = (name: string) => {
    updateSystem(activeSystem.id, (system) => ({
      ...system,
      themeName: name,
      updatedAt: new Date().toISOString(),
    }))
  }

  const setSystemName = (name: string) => {
    updateSystem(activeSystem.id, (system) => ({
      ...system,
      name,
      updatedAt: new Date().toISOString(),
    }))
  }

  const hasOverrides = Object.keys(activeSystem.overrides).length > 0

  return {
    systems: store.systems,
    activeSystem,
    resolvedTokens,
    selectSystem,
    createSystem,
    setMotif,
    updateToken,
    setThemeName,
    setSystemName,
    resetOverrides,
    hasOverrides,
  }
}
